import { Divider, Menu, type PaperProps } from "@mui/material"
import { useEffect, useLayoutEffect, useMemo, useState } from "react"
import {
  EmptyState,
  ResetOption,
  SearchField,
  TreeLeafItem,
  TreeParentItem,
} from "./components"
import {
  type TreeNode,
  computeNextValue,
  filterTree,
  getParentsToAutoExpand,
} from "./treeSelectMenu.utils"

export interface TreeSelectMenuProps<T extends string = string> {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  options: TreeNode<T>[]
  value?: T[]
  defaultValue?: T[]
  onChange?: (value: T[]) => void
  searchPlaceholder?: string
  resetLabel?: string
  emptyLabel?: string
  slotProps?: {
    paper?: PaperProps
  }
}

export const TreeSelectMenu = <T extends string = string>({
  anchorEl,
  open,
  onClose,
  options,
  value: controlledValue,
  defaultValue,
  onChange,
  slotProps,
  searchPlaceholder,
  resetLabel,
  emptyLabel,
}: TreeSelectMenuProps<T>) => {
  const isControlled = controlledValue !== undefined
  const [internalValue, setInternalValue] = useState<T[]>(defaultValue ?? [])
  const value = isControlled ? controlledValue : internalValue

  const emit = (next: T[]) => {
    if (!isControlled) setInternalValue(next)
    onChange?.(next)
  }

  const [expanded, setExpanded] = useState<Set<T>>(new Set())
  const [searchTerm, setSearchTerm] = useState("")
  const filteredOptions = useMemo(
    () => filterTree(options, searchTerm),
    [options, searchTerm],
  )
  const autoExpand = useMemo(
    () => getParentsToAutoExpand(filteredOptions, searchTerm),
    [filteredOptions, searchTerm],
  )

  const isParentOpen = (code: T) => expanded.has(code) || autoExpand.has(code)

  useEffect(() => {
    if (!open) {
      setSearchTerm("")
      setExpanded(new Set())
    }
  }, [open])

  // MUI Popover only repositions on open and on window "resize": force that
  // recalculation whenever the menu content size changes (expand, search).
  useLayoutEffect(() => {
    if (!open) return
    window.dispatchEvent(new Event("resize"))
  }, [open, expanded, searchTerm])

  const toggleExpand = (code: T) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  const computeAndEmitNextValue = (node: TreeNode<T>) => {
    emit(computeNextValue(node, value))
  }

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          ...slotProps?.paper,
        },
      }}
    >
      <SearchField
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder={searchPlaceholder}
      />
      <Divider />
      {value.length > 0 && (
        <ResetOption label={resetLabel} onReset={() => emit([])} />
      )}
      {filteredOptions.length === 0 && <EmptyState label={emptyLabel} />}
      {filteredOptions.map((node) =>
        node.children && node.children.length > 0 ? (
          <TreeParentItem
            key={node.code}
            node={node}
            value={value}
            isOpen={isParentOpen(node.code)}
            onToggleExpand={toggleExpand}
            onSelect={computeAndEmitNextValue}
          />
        ) : (
          <TreeLeafItem
            key={node.code}
            node={node}
            value={value}
            onSelect={computeAndEmitNextValue}
          />
        ),
      )}
    </Menu>
  )
}
